import React, { useEffect, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  type GroupedBarDimension,
} from "@store/slices/chart-config-slice";
import {
  resolveManagedItems,
  getGroupedVisibleCounts,
  reconcileGroupedChartData,
  groupedChartDataEquals,
} from "@utils/grouped-bar-utils";
import { useGroupedBarVariables } from "../hooks/useGroupedBarVariables";
import GroupedManagedList from "./GroupedManagedList";
import SelectionSummary from "./SelectionSummary";

interface GroupedCategorySectionProps {
  groupBy: GroupedBarDimension;
  isDisabled: boolean;
}

/**
 * Category presentation for grouped bar: the "Groups" and "Series" management
 * lists plus a summary. Every variable and value always appears in its list;
 * the researcher hides items rather than selecting them. Which set fills Groups
 * vs Series follows Group By; all per-item state is keyed by stable id, so a
 * Group By swap only changes which section an item appears in.
 */
const GroupedCategorySection: React.FC<GroupedCategorySectionProps> = ({
  groupBy,
  isDisabled,
}) => {
  const dispatch = useAppDispatch();
  const { currentConfig } = useAppSelector(chartConfigSelector);
  const { variables, values } = useGroupedBarVariables();

  const {
    toggleGroupedItemVisibility,
    setGroupedItemLabelOverride,
    reorderGroupedDimension,
    setGroupedChartData,
  } = chartConfigDispatcher(dispatch);

  const groupedBar = currentConfig.groupedBar;

  // Reconcile the fetched chart data ids to the datasetElementIds so visibility /
  // overrides / order match. The response uses different ids than the universe;
  // matching is done by label.
  useEffect(() => {
    const chartData = groupedBar?.chartData;
    if (!chartData) return;
    if (variables.length === 0 && values.length === 0) return;
    const reconciled = reconcileGroupedChartData(
      chartData,
      variables,
      values,
      groupBy,
    );
    if (!groupedChartDataEquals(chartData, reconciled)) {
      setGroupedChartData(reconciled);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedBar?.chartData, variables, values, groupBy]);

  const seriesDimension: GroupedBarDimension =
    groupBy === "variable" ? "value" : "variable";

  // Groups come from chartData.categories, series from chartData.series (the
  // response is already oriented by Group By). Labels come straight from the
  // data, so no id→label lookup and no raw-id flash.
  const groupItems = useMemo(
    () =>
      resolveManagedItems(
        groupedBar?.chartData?.categories ?? [],
        (groupBy === "variable"
          ? groupedBar?.variableOrder
          : groupedBar?.valueOrder) ?? [],
        (groupBy === "variable"
          ? groupedBar?.variableCustomizations
          : groupedBar?.valueCustomizations) ?? {},
      ),
    [groupedBar, groupBy],
  );
  const seriesItems = useMemo(
    () =>
      resolveManagedItems(
        groupedBar?.chartData?.series ?? [],
        (seriesDimension === "variable"
          ? groupedBar?.variableOrder
          : groupedBar?.valueOrder) ?? [],
        (seriesDimension === "variable"
          ? groupedBar?.variableCustomizations
          : groupedBar?.valueCustomizations) ?? {},
      ),
    [groupedBar, seriesDimension],
  );

  const { groups, series } = getGroupedVisibleCounts(groupedBar, groupBy);
  const warning =
    groups < 1
      ? "Show at least one group."
      : series < 2
        ? "Show at least two series."
        : undefined;

  return (
    <Box
      sx={{ p: 2, border: "1px solid #e0e0e0", borderRadius: 1 }}
      display="flex"
      flexDirection="column"
      gap={2}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Categories
      </Typography>

      <GroupedManagedList
        title="Groups"
        items={groupItems}
        isDisabled={isDisabled}
        helperText="Clusters shown along the axis."
        onToggleVisibility={(id) =>
          toggleGroupedItemVisibility({ dimension: groupBy, id })
        }
        onReorder={(order) =>
          reorderGroupedDimension({ dimension: groupBy, order })
        }
        onLabelChange={(id, label) =>
          setGroupedItemLabelOverride({ dimension: groupBy, id, label })
        }
        onResetLabel={(id) =>
          setGroupedItemLabelOverride({ dimension: groupBy, id, label: "" })
        }
      />

      <GroupedManagedList
        title="Series"
        items={seriesItems}
        isDisabled={isDisabled}
        helperText="Bars compared within each group."
        onToggleVisibility={(id) =>
          toggleGroupedItemVisibility({ dimension: seriesDimension, id })
        }
        onReorder={(order) =>
          reorderGroupedDimension({ dimension: seriesDimension, order })
        }
        onLabelChange={(id, label) =>
          setGroupedItemLabelOverride({ dimension: seriesDimension, id, label })
        }
        onResetLabel={(id) =>
          setGroupedItemLabelOverride({
            dimension: seriesDimension,
            id,
            label: "",
          })
        }
      />

      <Divider />

      <SelectionSummary groups={groups} series={series} warning={warning} />
    </Box>
  );
};

export default GroupedCategorySection;
